import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { ConstructAPI } from 'src/app/API/constructAPI';
import { RestApiService } from 'src/app/API/restapi';
import { CustomerAccount, IAPICustomerAccount } from 'src/model/customerAccount.model';
import { FileInterface } from 'src/model/file.interface';
import { FileAdapter, FileUrlBuilder } from './file.service';

@Injectable({
  providedIn: 'root'
})
export class CustomerAccountAdapter {

  adapt(item: any): CustomerAccount {
    return new CustomerAccount(
      item.id,
      item.bank,
      item.account_number,
      item.holder_name,
      new Date(item.created_at),
      item.created_by,
      item.customerId,
      item.contactId,
      item.remark,
      item.disabled_by,
      (item.disabled_on && new Date(item.disabled_on)),
      item.disabled_remark
    )
  }

  toApiModel(account: CustomerAccount): IAPICustomerAccount {
    return {
      ...(account.Id && { id: account.Id }),
      bank: account.Bank,
      account_number: account.AccountNumber,
      ...(account.HolderName && { holder_name: account.HolderName }),
      customerId: account.CustomerId,
      ...(account.ContactId && { contactId: account.ContactId }),
      remark: account.Remark,
      ...(account.CreatedAt && { created_at: account.CreatedAt }),
      ...(account.CreatedBy && { created_by: account.CreatedBy }),
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class CustomerAccountService {
  private api = inject(RestApiService);
  private constructApi = inject(ConstructAPI);
  private adapter = inject(CustomerAccountAdapter);
  private fileAdapter = inject(FileAdapter);
  private fileUrl = inject(FileUrlBuilder);

  getAddCompanyAccount(customerId: number, customerAccount: CustomerAccount) {

    return new Promise<CustomerAccount>((resolve, reject) => {
      // console.log(this.adapter.toApiModel(customerAccount))
      this.api.post<CustomerAccount>(this.constructApi.getAddCustomerAccount(customerId), this.adapter.toApiModel(customerAccount)).subscribe({
        next: response => {
          resolve(this.adapter.adapt(response));
        },
        error: err => {
          this.api.handleError(err);
          console.error('Failed to add customer account: ', err);
          reject(err);
        }
      })
    })
  }

  getAllCustomerAccounts(custIds: number[]): Promise<CustomerAccount[]> {
    return new Promise<CustomerAccount[]>((resolve, reject) => {
      this.api.get<CustomerAccount[]>(this.constructApi.getAllCustomerAccount(custIds)).pipe(
        map(data => data.map(d => this.adapter.adapt(d)
        ))
      ).subscribe(
        {
          next: (response) => { resolve(response) },
          error: err => {
            this.api.handleError(err);
            console.error('Failed to get all customer account: ', err);
            reject(err);
          }
        }
      )
    })
  }

  getAccountDetails(acctId: number): Promise<CustomerAccount> {
    return new Promise<CustomerAccount>((resolve, reject) => {
      this.api.get<CustomerAccount>(this.constructApi.getBankAccountById(acctId)).pipe(
        map(data => this.adapter.adapt(data))
      ).subscribe(
        {
          next: (response) => { resolve(response) },
          error: err => {
            this.api.handleError(err);
            console.error('Failed to get customer account: ', err);
            reject(err);
          }
        }
      )
    })
  }

  getAccountsDetails(acctIds: number[]): Promise<CustomerAccount[]> {
    return new Promise<CustomerAccount[]>((resolve, reject) => {
      this.api.get<CustomerAccount[]>(this.constructApi.getBankAccounts(acctIds)).pipe(
        map(data => data.map(d => this.adapter.adapt(d)
        ))
      ).subscribe(
        {
          next: (response) => { resolve(response) },
          error: err => {
            this.api.handleError(err);
            console.error('Failed to get all customer account: ', err);
            reject(err);
          }
        }
      )
    })
  }

  getCustomerAccountFiles(customerId: number, accountId: number): Promise<FileInterface[]> {
    return new Promise<FileInterface[]>((resolve, reject) => {
      this.api.get<FileInterface[]>(this.constructApi.getCustomerAcctAllFiles(customerId, accountId)).pipe(
        map(data => data.map(d => this.fileAdapter.adapt(d, this.fileUrl.buildCustomerFileUrl(customerId))))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to get customer account files: ', err);
          reject(err);
        }
      })
    })
  }

  postDisableCustomerAccount(customerId: number, accountId: number, remark: string): Promise<CustomerAccount> {
    return new Promise<CustomerAccount>((resolve, reject) => {
      this.api.post<CustomerAccount>(this.constructApi.getCustomerAccountDisable(customerId, accountId), { remark: remark }).subscribe({
        next: response => resolve(this.adapter.adapt(response)),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to disable customer account: ', err);
          reject(err);
        }
      })
    })
  }
}
